// PlanPopover — anchored selector for changing a user's plan kind.
//
// Picks between four states (free / weekly / monthly / yearly); the
// current one is highlighted + non-clickable. Selecting a different
// option immediately fires onChoose — the optimistic UI in the caller
// is the only "confirmation" step.
//
// Portalled to document.body so it survives table-row stacking contexts.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { EntitlementPlan, EntitlementTier, UserRow } from '@/lib/api';

export type PlanChoice =
  | 'free'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'ultra_weekly'
  | 'ultra_monthly'
  | 'ultra_yearly';

export const PLAN_LABELS: Record<PlanChoice, string> = {
  free: 'Free',
  weekly: 'Premium · Weekly',
  monthly: 'Premium · Monthly',
  yearly: 'Premium · Yearly',
  ultra_weekly: 'Ultra · Weekly',
  ultra_monthly: 'Ultra · Monthly',
  ultra_yearly: 'Ultra · Yearly',
};

/** Map a UserRow's (tier, plan) onto our PlanChoice display set. */
export function currentChoice(u: { tier: EntitlementTier; plan: EntitlementPlan }): PlanChoice {
  if (u.tier === 'free') return 'free';
  if (u.tier === 'ultra') {
    if (u.plan === 'yearly') return 'ultra_yearly';
    if (u.plan === 'weekly') return 'ultra_weekly';
    return 'ultra_monthly';
  }
  if (u.plan === 'yearly' || u.plan === 'weekly') return u.plan;
  return 'monthly';
}

/** Inverse — turn a UI choice into the backend's {tier, plan} body. */
export function choiceToBody(c: PlanChoice): { tier: EntitlementTier; plan: EntitlementPlan } {
  if (c === 'free') return { tier: 'free', plan: 'none' };
  if (c === 'ultra_weekly') return { tier: 'ultra', plan: 'weekly' };
  if (c === 'ultra_yearly') return { tier: 'ultra', plan: 'yearly' };
  if (c === 'ultra_monthly') return { tier: 'ultra', plan: 'monthly' };
  return { tier: 'premium', plan: c };
}

export function PlanPopover({
  anchor, user, onCancel, onChoose,
}: {
  anchor: HTMLElement;
  user: UserRow;
  onCancel: () => void;
  onChoose: (c: PlanChoice) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const current = currentChoice(user);
  const userLabel = user.display_name || user.email;

  useLayoutEffect(() => {
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const popW = 240;
      const popH = 260;
      let left = r.right + 8;
      const top = r.top + r.height / 2 - popH / 2;
      // Flip to the left side if the popover would spill off the right.
      if (left + popW > window.innerWidth - 8) {
        left = r.left - popW - 8;
      }
      if (left < 8) left = 8;
      const safeTop = Math.max(8, Math.min(top, window.innerHeight - 8 - popH));
      setPos({ top: safeTop, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (ref.current?.contains(t)) return;
      if (anchor.contains(t)) return;
      onCancel();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onCancel]);

  if (!pos) return null;

  const choices: PlanChoice[] = [
    'free',
    'weekly',
    'monthly',
    'yearly',
    'ultra_weekly',
    'ultra_monthly',
    'ultra_yearly',
  ];

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 240,
        background: 'var(--surface)',
        border: '1px solid var(--line-strong)',
        borderRadius: 14,
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
        padding: 12,
        zIndex: 1000,
      }}
    >
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        Set plan for
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 600, marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {userLabel}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {choices.map((c) => {
          const isActive = c === current;
          return (
            <button
              key={c}
              type="button"
              onClick={() => { if (!isActive) onChoose(c); }}
              disabled={isActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 12px',
                borderRadius: 10,
                border: 'none',
                background: isActive ? 'var(--bg)' : 'transparent',
                cursor: isActive ? 'default' : 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
                color: 'var(--ink)',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {PLAN_LABELS[c]}
              </span>
              {isActive && (
                <span className="mono" style={{ fontSize: 10, color: 'var(--coral)', letterSpacing: 1 }}>
                  CURRENT
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '6px 12px',
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-3)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
